import { createContext, useContext, useState, useEffect } from "react";

import alqemiDataService, { Offer } from "../../api/AlqemiDataService";
import { useNavbarOffer } from "../../common/context/NavbarOfferContext";

type OfferState = {
  offer: Offer;
  setOffer: (offer: Offer) => void;
  saveOffer: (
    newEmail: string,
    matchEmail: string,
    property: string
  ) => Promise<Offer>;
};

export const useOffer = (): OfferState => {
  const offerState = useContext(OfferContext);
  if (!offerState) {
    throw new Error("useOffer must be used within a OfferProvider");
  }
  return offerState;
};

const OfferContext = createContext<OfferState | null>(null);

type OfferProviderProps = {
  children: JSX.Element;
  preFetchedValues: [Offer];
};

const OfferProvider = ({ children, preFetchedValues }: OfferProviderProps) => {
  const [offer, setOffer] = useState<Offer>(preFetchedValues[0]);
  const { setOfferData } = useNavbarOffer();

  const formatProviderName = (provider?: string) => {
    if (!provider) return "Via Manual Entry";
    if (provider === "freelancermap") return "freelancermap.de";
    if (provider === "freelance_de") return "freelance.de";
    return provider;
  };

  // Update navbar offer data whenever the offer changes
  useEffect(() => {
    if (offer) {
      setOfferData({
        title: offer.title || "Untitled Offer",
        companyName: (offer.ansprechpartner && offer.ansprechpartner !== "null")
          ? offer.ansprechpartner
          : (offer.company && offer.company !== "null")
          ? offer.company
          : formatProviderName(offer.provider),
        url: offer.url,
        isArchived: !!offer.archived_at,
      });
    }

    // Clean up navbar data when component unmounts
    return () => {
      setOfferData({});
    };
  }, [offer, setOfferData]);

  const saveOffer = async (
    value: string,
    matchEmail: string,
    property: string
  ) => {
    const match = offer.matches?.find((m) => m.candidate?.email === matchEmail) || offer.matches?.[0];
    
    if (!match) {
      console.error("No match found for email:", matchEmail);
      return offer;
    }

    // For matches, we need to use consultant_id as the identifier
    const matchId = match.id || match.consultant_id;
    await alqemiDataService.updateMatch(matchId, property, value);

    const updatedOffer = { ...offer };
    if (updatedOffer.matches && match) {
      const matchIndex = updatedOffer.matches.findIndex((m) => m.candidate?.email === matchEmail);
      if (matchIndex !== -1) {
        updatedOffer.matches[matchIndex][property] = value;
      }
    }
    setOffer(updatedOffer);
    return updatedOffer;
  };

  return (
    <OfferContext.Provider
      value={{
        offer,
        setOffer,
        saveOffer,
      }}
    >
      {children}
    </OfferContext.Provider>
  );
};

export default OfferProvider;
